import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { api } from "../client";
import { Field } from "../ui";

const ITEMS = ["ignites", "calls", "roses", "reverses", "boosts"];
const CONFIG = {
  grant: { title: "Grant consumables", path: "consumables", cta: "Grant" },
  contact: { title: "Update contact info", path: "contact", cta: "Save" },
  warning: { title: "Issue warning", path: "warning", cta: "Send warning" },
  suspend: { title: "Suspend swipe & chat", path: "suspend", cta: "Suspend" },
  ban: { title: "Permanent ban", path: "ban", cta: "Ban permanently" },
};

const initial = (kind, user) => ({
  grant: Object.fromEntries([...ITEMS.map((k) => [k, 0]), ["reason", ""]]),
  contact: { email: user?.email || "", phone: user?.phone || "" },
  warning: { message: "" },
  suspend: { hours: 24, reason: "" },
  ban: { reason: "" },
}[kind] || {});

export default function UserActionDialog({ kind, user, onClose, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(initial(kind, user)); }, [kind, user]);
  if (!kind) return null;
  const cfg = CONFIG[kind];
  const set = (k, num) => (e) => setF({ ...f, [k]: num ? Math.max(0, parseInt(e.target.value || "0", 10)) : e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = kind === "contact" ? { email: f.email || null, phone: f.phone || null } : f;
    try { await api(`/staff/users/${user.user_id}/${cfg.path}`, { method: "POST", body }); toast.success(`${cfg.title}: done`); onDone(); }
    catch (e2) { toast.error(e2.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="staff-root border-[var(--s-line)] bg-[var(--s-panel)]" data-testid={`user-dialog-${kind}`}>
        <DialogHeader><DialogTitle className="s-display">{cfg.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {kind === "grant" && <div className="grid grid-cols-5 gap-2">{ITEMS.map((k) => <Field key={k} label={k}><Input type="number" min={0} value={f[k] ?? 0} onChange={set(k, true)} className="s-input" data-testid={`grant-${k}`} /></Field>)}</div>}
          {kind === "contact" && <>
            <Field label="Primary email"><Input type="email" value={f.email || ""} onChange={set("email")} className="s-input" data-testid="contact-email" /></Field>
            <Field label="Phone"><Input value={f.phone || ""} onChange={set("phone")} className="s-input" data-testid="contact-phone" /></Field>
          </>}
          {kind === "warning" && <Field label="Banner message"><Textarea required minLength={3} value={f.message || ""} onChange={set("message")} className="s-input" data-testid="warning-message" /></Field>}
          {kind === "suspend" && <div className="flex gap-2">{[[24, "24h"], [72, "72h"], [168, "7 days"]].map(([h, l]) => (
            <button type="button" key={h} onClick={() => setF({ ...f, hours: h })} data-testid={`suspend-${h}`}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${f.hours === h ? "border-[var(--s-accent)] bg-[var(--s-accent)] text-black" : "border-[var(--s-line)] hover:border-[var(--s-accent)]"}`}>{l}</button>
          ))}</div>}
          {["grant", "suspend", "ban"].includes(kind) && <Field label="Reason"><Textarea required={kind === "ban"} minLength={kind === "ban" ? 3 : 0} value={f.reason || ""} onChange={set("reason")} className="s-input" data-testid={`${kind}-reason`} /></Field>}
          {kind === "ban" && <p className="text-xs text-red-300">Revokes all sessions and blocks this device ID and Didit token from rejoining.</p>}
          <Button type="submit" disabled={busy} className={kind === "ban" ? "s-btn-danger w-full" : "s-btn w-full"} data-testid={`user-dialog-submit-${kind}`}>{busy ? "Working…" : cfg.cta}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
